"use client";

import { Box, Divider, Flex, Skeleton } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { MicroSiteHeaderSkeleton } from "@/components/feedback";
import { useThemeTokens } from "@/lib/theme/tokens";
import { PublicListingToolbar } from "./PublicListingToolbar";

export function PublicListingPageSkeleton({
  hideHero = false,
  toolbar,
  children,
}: {
  hideHero?: boolean;
  toolbar: ReactNode;
  children: ReactNode;
}) {
  const theme = useThemeTokens();

  return (
    <Flex
      direction="column"
      gap={{ base: 5, md: 6 }}
      maxW="1044px"
      mx="auto"
      w="100%"
    >
      {!hideHero ? <MicroSiteHeaderSkeleton /> : null}
      {hideHero ? (
        <Divider borderColor={theme.borderGlass.subtle} opacity={0.7} />
      ) : null}
      <Box>
        {toolbar}
        <Flex pt={2} pb={2}>
          <Skeleton height="12px" width="148px" borderRadius="4px" />
        </Flex>
        {children}
      </Box>
    </Flex>
  );
}

export function PublicListingToolbarSkeleton({
  variant,
}: {
  variant: "classes" | "selectSearch";
}) {
  return (
    <PublicListingToolbar>
      <Flex
        display={{ base: "flex", md: "none" }}
        gap="10px"
        w="100%"
        minW={0}
        overflow="hidden"
        align="center"
      >
        <Skeleton
          height="44px"
          width="108px"
          borderRadius="8px"
          flexShrink={0}
        />
        <Skeleton height="44px" flex={1} minW={0} borderRadius="8px" />
      </Flex>

      {variant === "classes" ? (
        <>
          <Box
            display={{ base: "none", md: "block" }}
            flex={{ md: 1 }}
            minW={{ md: 0 }}
            maxW={{ md: "300px" }}
            sx={{
              "@media (min-width: 1035px)": {
                maxWidth: "none",
              },
            }}
          >
            <Flex gap="10px" wrap="wrap">
              <Skeleton height="44px" width="145px" borderRadius="8px" />
              <Skeleton height="44px" width="145px" borderRadius="8px" />
              <Skeleton height="44px" width="145px" borderRadius="8px" />
              <Skeleton height="44px" width="145px" borderRadius="8px" />
            </Flex>
          </Box>
          <Box
            display={{ base: "none", md: "block" }}
            minW={{ md: "280px" }}
            maxW={{ md: "420px" }}
            flexShrink={{ md: 0 }}
          >
            <Skeleton height="44px" width="100%" borderRadius="8px" />
          </Box>
        </>
      ) : (
        <>
          <Box
            display={{ base: "none", md: "block" }}
            flex={1}
            minW={{ md: "220px" }}
          >
            <Skeleton height="44px" width="220px" borderRadius="8px" />
          </Box>
          <Box
            display={{ base: "none", md: "block" }}
            minW={{ md: "320px" }}
            maxW={{ md: "420px" }}
            flexShrink={{ md: 0 }}
          >
            <Skeleton height="44px" width="100%" borderRadius="8px" />
          </Box>
        </>
      )}
    </PublicListingToolbar>
  );
}

export function PublicListingRowsSkeleton({
  count,
  children,
}: {
  count: number;
  children: (index: number, isLast: boolean) => ReactNode;
}) {
  const theme = useThemeTokens();

  return (
    <Box borderTopWidth="1px" borderColor={theme.borderGlass.subtle}>
      {Array.from({ length: count }, (_, index) =>
        children(index, index === count - 1),
      )}
    </Box>
  );
}
